import { compose, join, map, invoker } from 'ramda';
import { ensureScript } from '../../../scriptLoader';

import {
    paramDestPath,
    paramParentPath,
    paramDsName,
} from '../constants/parameterNames';
import {
    typeXPath,
    typeDataSource,
    typeBoolean,
    typeOption,
} from '../constants/parameterTypes';
import { xmlToString } from '../../../xmlUtility';

const paramBeforePath = 'beforepath';
const paramAfterPath = 'afterpath';
const paramAddStyles = 'addstyles';
const paramImplementation = 'implementation';
const IMPLEMENTATION_TWINC = 'tnwinc';
const IMPLEMENTATION_DFOVERDX = 'dfoverdx';
const defaultImplementation = IMPLEMENTATION_DFOVERDX;
const tnwincUrl = 'https://github.com/tnwinc/htmldiff.js';
const dfoverdxUrl = 'https://github.com/dfoverdx/htmldiff-js';

const requiredParameters = [
    {
        name: paramBeforePath,
        description: `XPath to the "before" rich text value. Relative to /${paramParentPath} if it is specified.`,
        type: typeXPath,
    },
    {
        name: paramAfterPath,
        description: `XPath to the "after" rich text value. Relative to /${paramParentPath} if it is specified.`,
        type: typeXPath,
    },
];

const optionalParameters = [
    {
        name: paramParentPath,
        description: `Parent of /${paramBeforePath}, /${paramAfterPath}, and /${paramDestPath}. When specified, the command will go through all nodes that match the /${paramParentPath} and process the /${paramBeforePath} and /${paramAfterPath} relative to them.`,
        type: typeXPath,
    },
    {
        name: paramDestPath,
        description: `XPath where the diff result should be placed. Required if /${paramParentPath} is specified.  Relative to /${paramParentPath} if it is specified. If unspecified, the diff result is returned to the qRules Result field.`,
        type: typeXPath,
    },
    {
        name: paramDsName,
        description:
            'Data source where the rich text comparisons should be performed.',
        type: typeDataSource,
    },
    {
        name: paramAddStyles,
        description:
            '(Boolean, defaults to false) Adds CSS styles to the page to highlight insertions and deletions in green and red.',
        type: typeBoolean,
    },
    {
        name: paramImplementation,
        description: `The rich text diffing implementation to use. Possible options are ${IMPLEMENTATION_TWINC} (${tnwincUrl}), dfoverdx(${dfoverdxUrl}).Default is ${IMPLEMENTATION_DFOVERDX}`,
        type: typeOption,
        options: [
            {
                name: `${IMPLEMENTATION_TWINC} - ${tnwincUrl}`,
                value: IMPLEMENTATION_TWINC,
            },
            {
                name: `${IMPLEMENTATION_DFOVERDX} - ${dfoverdxUrl}`,
                value: IMPLEMENTATION_DFOVERDX,
            },
        ],
    },
];

const description =
    'Compares two rich text values, or multiple pairs of rich text values, and produces a rich text diff of their values.';
const nodeNotFound = (paramName) => `Could not find /${paramName} node`;
const errorNoBeforeNode = nodeNotFound(paramBeforePath);
const errorNoAfterNode = nodeNotFound(paramAfterPath);
const errorNoDestNode = nodeNotFound(paramDestPath);

const resourceNameAttribute = 'data-fv-resource-name';
const styleResourceName = '##richTextDiff';

function validateParameters(parent, dest) {
    if (parent && !dest) {
        throw new Error(
            `/${paramDestPath} must be specified if /${paramParentPath} is.`,
        );
    }
}

function ensureStyles() {
    const alreadyAdded = document.querySelector(
        `style[${resourceNameAttribute} = "${styleResourceName}"]`,
    );

    if (!alreadyAdded) {
        const el = document.createElement('style');
        el.setAttribute(resourceNameAttribute, styleResourceName);
        el.innerHTML =
            '.fvForm del { background-color: #fbb; }  .fvForm ins { background-color: #d4fcbc; }';
        document.head.appendChild(el);
    }
}

function getImplementationScriptKey(implementation) {
    switch (implementation) {
        case IMPLEMENTATION_TWINC:
            return 'htmldiff-tnwinc';
        case IMPLEMENTATION_DFOVERDX:
            return 'htmldiff';
        default:
            throw new Error(`Invalid implementation value: ${implementation}`);
    }
}

// DataSourceNode -> String
const serializeChildren = compose(
    join(''),
    map(xmlToString),
    map(invoker(0, 'getNode')),
    invoker(0, 'childNodes'),
);

function DiffRichText(params) {
    const { commonFunctions } = params;
    const {
        getValidNode,
        getParamValue,
        getBoolParamValue,
        getDataSource,
        getValidNodeSet,
    } = commonFunctions;

    const executeDiff = (htmldiff, startNode, before, after) => {
        const beforeValue = serializeChildren(
            getValidNode(startNode, before, errorNoBeforeNode),
        );
        const afterValue = serializeChildren(
            getValidNode(startNode, after, errorNoAfterNode),
        );

        return htmldiff(beforeValue, afterValue);
    };

    const executeDiffs = async (
        diffImpl,
        ds,
        beforePath,
        afterPath,
        parentPath,
        destPath,
    ) => {
        if (!parentPath) {
            return executeDiff(diffImpl, ds, beforePath, afterPath);
        }

        const nodes = getValidNodeSet(ds, parentPath);

        for (const parentNode of nodes) {
            const diff = executeDiff(
                diffImpl,
                parentNode,
                beforePath,
                afterPath,
            );

            const destNode = getValidNode(
                parentNode,
                destPath,
                errorNoDestNode,
            );

            await destNode.setValueAsync(diff, { isRichTextString: true });
        }

        return null;
    };

    async function executeAsync() {
        const before = getParamValue(paramBeforePath);
        const after = getParamValue(paramAfterPath);
        const parent = getParamValue(paramParentPath, null);
        const dest = getParamValue(paramDestPath, null);
        const dsname = getParamValue(paramDsName);
        const addStyles = getBoolParamValue(paramAddStyles);
        const implementation = getParamValue(
            paramImplementation,
            defaultImplementation,
        );

        validateParameters(parent, dest);

        const ds = getDataSource(dsname);

        if (addStyles) {
            ensureStyles();
        }

        const diffImpl = await ensureScript(
            getImplementationScriptKey(implementation),
        );

        const result = await executeDiffs(
            diffImpl,
            ds,
            before,
            after,
            parent,
            dest,
        );

        return {
            result: result || '',
            success: true,
        };
    }

    return {
        description,
        executeAsync,
        optionalParameters,
        requiredParameters,
    };
}

export default DiffRichText;
